import { BaseApiService } from './baseApiService';
import { environment } from '../../config/environment';

/**
 * Subscription Plan Interface
 * Represents a billing plan available for clinics
 */
export interface SubscriptionPlan {
  id: number;
  name: string;
  slug: string;
  description: string;
  price: string;
  features: {
    voice_calls?: boolean;
    text_messaging?: boolean;
    sms_notifications?: boolean;
    email_notifications?: boolean;
    voicemail?: boolean;
    call_recording?: boolean;
    // Pricing-tier presentation (Clinical Capture / Orchestration / Care Engine).
    highlights?: string[];
    popular?: boolean;
  };
  is_active: boolean;
  display_order: number;
}

export type SignupAccessMode = 'DIRECT_SIGNUP' | 'APPROVAL_REQUIRED';

export interface RegistrationPlan extends SubscriptionPlan {
  signup_access_mode: SignupAccessMode;
  is_signup_selectable: boolean;
}

export interface RegistrationPlansResponse {
  status: string;
  message: string;
  data: {
    plans: RegistrationPlan[];
    calcom_booking_url: string;
    trial_days: number;
  };
}

/**
 * Checkout Session Response
 * Contains the Stripe checkout URL to redirect user to
 */
export interface CheckoutSessionResponse {
  status: string;
  message: string;
  data: {
    url: string;
  };
}

/**
 * Subscription Status Response
 * Contains current subscription details for a clinic
 */
export interface SubscriptionStatus {
  status: string;
  message: string;
  data: {
    billing_status: string;
    seat_quantity: number;
    current_period_end: string;
    stripe_customer_id: string;
    stripe_subscription_id: string;
    plan_id?: number;
    plan_name?: string;
    plan_slug?: string;
    pending_plan_change?: {
      plan_id: number;
      plan_name: string;
      pending_seat_quantity?: number;
      effective_date: string;
      invoice_preview?: {
        amount_due: number;
        currency: string;
        period_start: string;
        period_end: string;
      };
    };
  };
}

/**
 * Plans Response
 * API response wrapper for subscription plans
 */
export interface PlansResponse {
  status: string;
  message: string;
  data: SubscriptionPlan[];
}

/**
 * Admin Plan (super-admin plan editor).
 * Carries the full feature-entitlement booleans plus the per-doctor monthly
 * session cap. Distinct from the public SubscriptionPlan whose `features` only
 * surfaces presentation flags.
 */
export interface AdminPlanFeatures {
  pre_consultation?: boolean;
  referral_letter_generation?: boolean;
  automated_workflows?: boolean;
  pdf_ingestion?: boolean;
  vbc_analytics?: boolean;
  chroniq?: boolean;
  patient_reported_outcomes?: boolean;
  multilingual_consult?: boolean;
  plato_integration?: boolean;
  custom_agent_prompts?: boolean;
  care_gaps?: boolean;
  [key: string]: boolean | undefined;
}

export interface AdminPlan {
  id: number;
  name: string;
  slug?: string;
  description?: string;
  price?: string;
  features: AdminPlanFeatures;
  session_limit: number | null;
  is_active: boolean;
  display_order?: number;
  signup_access_mode: SignupAccessMode;
}

export interface AdminPlansResponse {
  status: string;
  message: string;
  data: AdminPlan[];
}

export interface AdminPlanResponse {
  status: string;
  message: string;
  data: AdminPlan;
}

/** Patch body for updating a plan (all fields optional). */
export interface AdminPlanPatch {
  features?: AdminPlanFeatures;
  session_limit?: number | null;
  name?: string;
  price?: string;
  is_active?: boolean;
  signup_access_mode?: SignupAccessMode;
}

export interface ApprovedSignupPlan {
  id: number;
  name: string;
  slug: string;
  signup_access_mode: SignupAccessMode;
}

export interface ClinicSignupPlanApprovalResponse {
  status: string;
  message: string;
  data: {
    clinic_id: number;
    approved_signup_plan: ApprovedSignupPlan | null;
  };
}

/**
 * Billing Warning Response
 * Contains warning information and billing status
 */
export interface BillingWarningsResponse {
  status: string;
  message: string;
  data: {
    has_warning: boolean;
    warning: {
      severity: 'info' | 'warning' | 'error';
      message: string;
      action: string;
    } | null;
    billing_info: {
      has_subscription: boolean;
      billing_status: string;
      is_active: boolean;
      is_problematic: boolean;
      needs_attention: boolean;
      current_period_end: string;
      seat_quantity: number;
    };
  };
}

/**
 * Change Plan Response
 * Confirmation of plan change scheduling
 */
export interface ChangePlanResponse {
  status: string;
  message: string;
  data: {
    message: string;
    new_plan: string;
    new_quantity: number;
    effective_date: string;
    current_period_end: string;
  };
}

/**
 * Schedule Seat Change Response
 * Confirmation of seat quantity change scheduling
 */
export interface ScheduleSeatChangeResponse {
  status: string;
  message: string;
  data: {
    message: string;
    current_quantity: number;
    new_quantity: number;
    effective_date: string;
    current_period_end: string;
  };
}

/**
 * Portal Session Response
 * Contains Stripe billing portal URL
 */
export interface PortalSessionResponse {
  status: string;
  message: string;
  data: {
    url: string;
  };
}

/**
 * Resolved plan returned by the public plan-resolution endpoint.
 * TODO(backend-contract): GET /billing/plans/resolve/?code=...
 */
export interface ResolvedPlan {
  code: string;
  plan_id: number;
  name: string;
  slug: string;
  price: string;
  billing_interval: string;
  trial_days: number;
  features: string[];
}

export interface ResolvedPlanResponse {
  status?: string;
  message?: string;
  data?: ResolvedPlan;
  // Some endpoints return the plan unwrapped; support both.
  code?: string;
  plan_id?: number;
  name?: string;
  slug?: string;
  price?: string;
  billing_interval?: string;
  trial_days?: number;
  features?: string[];
}

/**
 * Registration subscribe response — kicks off Stripe hosted checkout.
 * TODO(backend-contract): POST /billing/registration/subscribe/
 */
export interface RegistrationSubscribeResponse {
  checkout_url: string;
  clinic_id: number | null;
  trial_days: number;
}

/**
 * Registration verify response — confirms trial/subscription after Stripe.
 * TODO(backend-contract): GET /billing/registration/verify/
 */
export interface RegistrationVerifyResponse {
  subscription_active: boolean;
  billing_status: string;
  seat_quantity?: number;
  can_proceed: boolean;
  registration_status?: Record<string, unknown>;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  user_context?: {
    entitlements?: Record<string, boolean | number | string | null>;
    [key: string]: unknown;
  };
}

class BillingService extends BaseApiService {
  /**
   * Resolve a plan by its public code (no auth required).
   * TODO(backend-contract): GET /billing/plans/resolve/?code=CLINICAL_CAPTURE
   * @param code - The public plan code (e.g. from the registration URL hash)
   */
  async getPlanByCode(code: string): Promise<ResolvedPlan> {
    const response = await fetch(
      `${environment.AUTH_SEC_API_URL}/billing/plans/resolve/?code=${encodeURIComponent(code)}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to resolve plan: ${response.statusText}`);
    }

    const body: ResolvedPlanResponse = await response.json();
    // Support both wrapped ({data}) and unwrapped shapes.
    if (body.data) {
      return body.data;
    }
    return {
      code: body.code || code,
      plan_id: body.plan_id ?? 0,
      name: body.name || '',
      slug: body.slug || '',
      price: body.price || '0',
      billing_interval: body.billing_interval || '',
      trial_days: body.trial_days ?? 0,
      features: body.features || [],
    };
  }

  /**
   * Subscribe during registration. Reuses the authenticated registration flow.
   * TODO(backend-contract): POST /billing/registration/subscribe/
   * @param params - Either a plan_id or a plan code
   */
  async registrationSubscribe(params: { plan_id?: number; code?: string; quantity?: number }): Promise<RegistrationSubscribeResponse> {
    const response = await this.fetchWithAuthUrl<{ data: RegistrationSubscribeResponse }>(
      '/billing/registration/subscribe/',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      }
    );
    return response.data;
  }

  /**
   * Verify the registration subscription/trial after returning from Stripe.
   * TODO(backend-contract): GET /billing/registration/verify/
   */
  async registrationVerify(): Promise<RegistrationVerifyResponse> {
    const response = await this.fetchWithAuthUrl<{ data: RegistrationVerifyResponse }>(
      '/billing/registration/verify/',
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    return response.data;
  }

  /** Get plans and clinic-specific signup access for the registration wizard. */
  async getRegistrationPlans(): Promise<RegistrationPlansResponse['data']> {
    const response = await this.fetchWithAuthUrl<RegistrationPlansResponse>(
      '/billing/registration/plans/',
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    return response.data;
  }

  /**
   * Get all available subscription plans
   * @returns Promise with list of available subscription plans
   */
  async getAvailablePlans(): Promise<SubscriptionPlan[]> {
    const response = await this.fetchWithAuthUrl<PlansResponse>('/billing/plans/', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  /**
   * Create a checkout session for subscription purchase
   * @param clinicId - The clinic ID
   * @param planId - The selected plan ID
   * @param quantity - Number of seats/licenses
   * @returns Promise with Stripe checkout URL
   */
  async createCheckoutSession(
    clinicId: number,
    planId: number,
    quantity: number
  ): Promise<CheckoutSessionResponse['data']> {
    const response = await this.fetchWithAuthUrl<CheckoutSessionResponse>(`/billing/clinics/${clinicId}/checkout-session/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        plan_id: planId,
        quantity: quantity,
      }),
    });
    return response.data;
  }

  /**
   * Get subscription status for a clinic
   * @param clinicId - The clinic ID
   * @returns Promise with subscription status details
   */
  async getSubscriptionStatus(clinicId: number): Promise<SubscriptionStatus['data']> {
    const response = await this.fetchWithAuthUrl<SubscriptionStatus>(`/billing/clinics/${clinicId}/subscription/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  /**
   * Get billing warnings for current user
   * @returns Promise with billing warnings and status
   */
  async getBillingWarnings(): Promise<BillingWarningsResponse['data']> {
    const response = await this.fetchWithAuthUrl<BillingWarningsResponse>('/billing/warnings/', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  /**
   * Change subscription plan
   * @param clinicId - The clinic ID
   * @param newPlanId - The new plan ID
   * @param newQuantity - The new seat quantity
   * @returns Promise with plan change confirmation
   */
  async changePlan(
    clinicId: number,
    newPlanId: number,
    newQuantity: number
  ): Promise<ChangePlanResponse['data']> {
    const response = await this.fetchWithAuthUrl<ChangePlanResponse>(`/billing/clinics/${clinicId}/change-plan/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        new_plan_id: newPlanId,
        new_quantity: newQuantity,
      }),
    });
    return response.data;
  }

  /**
   * Schedule seat quantity change
   * @param clinicId - The clinic ID
   * @param newQuantity - The new seat quantity
   * @returns Promise with seat change confirmation
   */
  async scheduleSeatChange(
    clinicId: number,
    newQuantity: number
  ): Promise<ScheduleSeatChangeResponse['data']> {
    const response = await this.fetchWithAuthUrl<ScheduleSeatChangeResponse>(`/billing/clinics/${clinicId}/schedule-seat-change/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        new_quantity: newQuantity,
      }),
    });
    return response.data;
  }

  /**
   * Super-admin: list all plans with their full feature-entitlement booleans
   * and session limits.
   * GET /api/v1/billing/admin/plans/
   */
  async getAdminPlans(): Promise<AdminPlan[]> {
    const response = await this.fetchWithAuthUrl<AdminPlansResponse>('/billing/admin/plans/', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  /**
   * Super-admin: update a plan's feature booleans, session_limit, name, price,
   * or active flag.
   * PATCH /api/v1/billing/admin/plans/<id>/
   * @param id - The plan ID
   * @param patch - Partial fields to update
   */
  async updateAdminPlan(id: number, patch: AdminPlanPatch): Promise<AdminPlan> {
    const response = await this.fetchWithAuthUrl<AdminPlanResponse>(`/billing/admin/plans/${id}/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(patch),
    });
    return response.data;
  }

  /** Super-admin: approve one restricted signup plan for one clinic, or revoke it. */
  async updateClinicSignupPlanApproval(
    clinicId: number,
    planId: number | null
  ): Promise<ClinicSignupPlanApprovalResponse['data']> {
    const response = await this.fetchWithAuthUrl<ClinicSignupPlanApprovalResponse>(
      `/billing/admin/clinics/${clinicId}/signup-plan-approval/`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: planId }),
      }
    );
    return response.data;
  }

  /**
   * Force a token refresh so freshly-changed entitlements take effect without
   * a re-login. Exposed here because billing flows (plan changes) need it.
   * Returns true on success.
   */
  async refreshEntitlements(): Promise<boolean> {
    return this.forceTokenRefresh();
  }

  /**
   * Create Stripe billing portal session
   * @param clinicId - The clinic ID
   * @returns Promise with Stripe portal URL
   */
  async createPortalSession(clinicId: number): Promise<PortalSessionResponse['data']> {
    const response = await this.fetchWithAuthUrl<PortalSessionResponse>(`/billing/clinics/${clinicId}/portal-session/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }
}

export const billingService = new BillingService();
